import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { useTranslations } from "next-intl";

type Props = {
  initialHtml: string;
  onChange: (html: string) => void;
};

export type TextLessonEditorHandle = {
  getHtml: () => string;
};

export const TextLessonEditor = forwardRef<TextLessonEditorHandle, Props>(function TextLessonEditor(
  { initialHtml, onChange },
  ref,
) {
  const t = useTranslations("instructor");
  const tTools = useTranslations("instructorCourseTools");
  const editor = useEditor({
    extensions: [StarterKit],
    content: initialHtml || "<p></p>",
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class:
          "prose prose-neutral max-w-none min-h-[160px] rounded border border-[var(--border)] px-3 py-2 focus:outline-none dark:prose-invert",
        "data-testid": "text-lesson-editor",
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
  });

  useImperativeHandle(
    ref,
    () => ({
      getHtml: () => editor?.getHTML() ?? initialHtml,
    }),
    [editor, initialHtml],
  );

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (initialHtml !== current) {
      editor.commands.setContent(initialHtml || "<p></p>", { emitUpdate: false });
    }
  }, [editor, initialHtml]);

  if (!editor) {
    return <p className="text-sm text-[var(--text-muted)]">{t("editorLoading")}</p>;
  }

  return (
    <div className="space-y-2" data-testid="text-lesson-editor-root">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded border px-2 py-1 text-xs"
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          {tTools("editorBold")}
        </button>
        <button
          type="button"
          className="rounded border px-2 py-1 text-xs"
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          {tTools("editorItalic")}
        </button>
        <button
          type="button"
          className="rounded border px-2 py-1 text-xs"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          {tTools("editorSubheading")}
        </button>
        <button
          type="button"
          className="rounded border px-2 py-1 text-xs"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          {tTools("editorBulletList")}
        </button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
});
